import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';

interface VendorPageHeaderProps {
  vendorName: string;
  onBack: () => void;
  /** Vendor wordmark (imported SVG URL). When given it is the visible title; the name stays as an sr-only h1. */
  vendorLogo?: string;
  /** Rendered wordmark height; the prototype tunes it per vendor for optical balance. */
  logoHeightPx?: number;
  /** Right-aligned status slot (IntegrationStatusLabel). */
  trailing?: ReactNode;
}

/**
 * Shared page header for vendor configuration pages: back-to-integrations
 * arrow, the vendor wordmark (prototype) with the vendor name kept as a
 * visually hidden h1 for the accessibility tree, and a right-aligned status
 * slot. Used by Rentvine, Buildium, and AppFolio configuration pages.
 */
export const VendorPageHeader = ({ vendorName, onBack, vendorLogo, logoHeightPx, trailing }: VendorPageHeaderProps) => (
  <header className="flex items-center gap-2 border-b border-border bg-card px-4 py-3">
    <Button
      variant="ghost"
      size="icon"
      type="button"
      onClick={onBack}
      tooltip="Back to Integrations"
      aria-label="Back"
    >
      <ArrowLeft className="h-5 w-5" />
    </Button>
    {vendorLogo ? (
      <>
        <img
          src={vendorLogo}
          alt=""
          style={{ height: logoHeightPx ?? 24 }}
          className="w-auto max-w-[170px] object-contain"
        />
        <Typography as="h1" className="sr-only">{vendorName}</Typography>
      </>
    ) : (
      <Typography as="h1" variant="heading-xl">{vendorName}</Typography>
    )}
    {trailing && <div className="ml-auto flex items-center pr-2">{trailing}</div>}
  </header>
);
